'use client';

import { usePathname, useRouter } from 'next/navigation';
import qs from 'qs';
import { createContext, useContext, useEffect, useState } from 'react';
import { resolveSettledValue, setCookie } from 'utils';

import { fetchBrandsFilterList } from '@/modules/brands/api';
import { ProductPageBannerLocation } from '@/modules/product/api/banners';
import {
  fetchFilterList,
  fetchPromotionsFiltersList,
  fetchSelloutsFiltersList,
  FilterListResponse,
} from '@/modules/product/api/productList';
import { OrderBy } from '@/shared/types/order-by';

import { BANNERS_STATE_COOKIE_NAME } from '../../utils';
import {
  ProductListContextProviderProps,
  ProductListContext as ProductListContextType,
} from './types';

const ProductListContext = createContext<ProductListContextType>({
  categorySlug: '',
  categoryThree: [],
  crossRewrite: [],
  brandName: '',
  brandSlug: '',
  ascending: 1,
  filters: {},
  filterList: [],
  filteredFilterList: [],
  isFiltersListLoaded: false,
  isLoading: false,
  limit: 48,
  orderBy: 'name',
  page: 1,
  query: '',
  total: 0,
  products: [],
  defaultCalendar: {},
  localeCalendar: {},
  shippingDeliverySettings: {
    isCutoff: 0,
    immediateShippingDelay: 0,
    logisticDelay: 0,
  },
  breadcrumbs: [],
  banners: {
    product_grid_pos_39: [],
    product_grid_pos_15: [],
    inside_grid: [],
    above_footer: [],
  },
  productMenuOpen: true,
  productMenuMobileSidebarOpen: false,
  bannersState: {
    above_footer: 0,
    inside_grid: 0,
    product_grid_pos_15: 0,
    product_grid_pos_39: 0,
  },
  seo: {
    bottomDescription: '',
    locale: '',
    meta: {
      description: '',
      title: '',
      canonical: null,
    },
    canonical: '',

    robots: '',
    topDescription: {
      h1: '',
      popularCollections: [],
    },
    url: '',
  },
  setProductMenuMobileSidebarOpen: () => {},
  setProductMenuOpen: () => {},
  handleFetchFilters: (
    newAscending: number,
    newOrderBy: OrderBy,
    newFilters: Record<string, unknown>
  ) => {},
  handleFetchNewPage: (newPage: number) => {},
  setAscending: () => {},
  setFilters: () => {},
  setFilterList: () => {},
  setFilteredFilterList: () => {},
  setIsFiltersListLoaded: () => {},
  setIsLoading: () => {},
  setLimit: () => {},
  setOrderBy: () => {},
  setPage: () => {},
  setQuery: () => {},
  setTotal: () => {},
  setProducts: () => {},
  isRewritePage: false,
});

export const ProductListProvider = ({
  breadcrumbs,
  banners,
  children,
  categoryThree,
  categoryThreeOpenInitialState,
  crossRewrite,
  initialProducts,
  initialAscending,
  initialFilters,
  initialOrderBy,
  initialPage,
  initialQuery,
  initialTotal,
  initialLimit,
  bannersInitialState,
  brandName,
  brandSlug,
  categorySlug,
  defaultCalendar,
  localeCalendar,
  shippingDeliverySettings,
  seo,
  isRewritePage,
}: ProductListContextProviderProps) => {
  const router = useRouter();
  const [products, setProducts] = useState(initialProducts);
  const [ascending, setAscending] = useState(initialAscending);
  const [filters, setFilters] = useState(initialFilters);
  const [isFiltersListLoaded, setIsFiltersListLoaded] = useState(
    categorySlug === 'search'
  );
  const [filterList, setFilterList] = useState<FilterListResponse>([]);
  const [filteredFilterList, setFilteredFilterList] =
    useState<FilterListResponse>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(initialLimit);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [page, setPage] = useState(initialPage);
  const [query, setQuery] = useState(initialQuery);
  const [total, setTotal] = useState(initialTotal);
  const [productMenuOpen, setProductMenuOpen] = useState(
    categoryThreeOpenInitialState === 'true'
  );
  const [productMenuMobileSidebarOpen, setProductMenuMobileSidebarOpen] =
    useState(false);
  const [bannersState, setBannerState] = useState<
    Record<ProductPageBannerLocation, number>
  >(() => {
    if (!bannersInitialState) {
      const initialObject = {
        product_grid_pos_15: 0,
        product_grid_pos_39: 0,
        inside_grid: 0,
        above_footer: 0,
      };
      setCookie(BANNERS_STATE_COOKIE_NAME, JSON.stringify(initialObject));
      return initialObject;
    }

    return JSON.parse(bannersInitialState);
  });
  const pathname = usePathname();

  let baseSlug: string;
  if (isRewritePage) {
    baseSlug = pathname.replace(/^(\/[^/]+).*$/, `$1/${categorySlug}`);
  } else {
    baseSlug = pathname;
  }

  const handleURLUpdate = (
    pageParam: number,
    ascendingParam: number,
    queryParam: string,
    filtersParam: Record<string, unknown>,
    orderByParam: OrderBy
  ) => {
    const newPage = pageParam === 1 ? undefined : pageParam;
    const newAscending =
      ascendingParam === 0 && orderByParam === 'sales_ratio'
        ? undefined
        : ascendingParam;
    const newOrderBy =
      ascendingParam === 0 && orderByParam === 'sales_ratio'
        ? undefined
        : orderByParam;

    const parsedQuery = qs.stringify(
      {
        page: newPage,
        ascending: newAscending,
        query: queryParam,
        filters: filtersParam,
        orderBy: newOrderBy,
      },
      {
        arrayFormat: 'brackets',
        encode: false,
        filter: (prefix, value) =>
          value !== undefined && value !== '' ? value : undefined,
      }
    );

    const parsedCurrent = qs.stringify(
      {
        page: page === 1 ? undefined : page,
        ascending:
          ascending === 0 && orderBy === 'sales_ratio' ? undefined : ascending,
        query,
        filters,
        orderBy:
          ascending === 0 && orderBy === 'sales_ratio' ? undefined : orderBy,
      },
      {
        arrayFormat: 'brackets',
        encode: false,
        filter: (prefix, value) =>
          value !== undefined && value !== '' ? value : undefined,
      }
    );

    if (parsedQuery === parsedCurrent) {
      return;
    }

    setIsLoading(true);
    setIsFiltersListLoaded(categorySlug === 'search');

    let newUrl = baseSlug || '';
    if (parsedQuery !== '') {
      newUrl = `${baseSlug}?${parsedQuery}`;
    }

    router.push(newUrl);
  };

  const handleFetchNewPage = (newPage: number) => {
    handleURLUpdate(newPage, ascending, query, filters, orderBy);
  };

  const handleFetchFilters = (
    newAscending: number,
    newOrderBy: OrderBy,
    newFilters: Record<string, unknown>
  ) => {
    handleURLUpdate(1, newAscending, query, newFilters, newOrderBy);
  };

  function loadFilters() {
    if (categorySlug === 'search') {
      return;
    }

    setFilterList([]);
    setFilteredFilterList([]);
    setIsFiltersListLoaded(false);

    (brandSlug
      ? Promise.allSettled([
          fetchBrandsFilterList(brandSlug),
          fetchBrandsFilterList(brandSlug, initialFilters ?? {}),
        ])
      : categorySlug === 'promotions'
        ? Promise.allSettled([
            fetchPromotionsFiltersList(),
            fetchPromotionsFiltersList(initialFilters ?? {}),
          ])
        : categorySlug === 'sellouts'
          ? Promise.allSettled([
              fetchSelloutsFiltersList(),
              fetchSelloutsFiltersList(initialFilters ?? {}),
            ])
          : Promise.allSettled([
              fetchFilterList(categorySlug, {}),
              fetchFilterList(categorySlug, initialFilters ?? {}),
            ])
    ).then(([filtersListResult, filteredFiltersListResult]) => {
      setFilterList(resolveSettledValue(filtersListResult, []));
      setFilteredFilterList(resolveSettledValue(filteredFiltersListResult, []));
      setIsFiltersListLoaded(true);
    });
  }

  useEffect(() => {
    setAscending(initialAscending);
    setFilters(initialFilters);
    setOrderBy(initialOrderBy);
    setPage(initialPage);
    setProducts(initialProducts);
    setQuery(initialQuery);
    setTotal(initialTotal);
    setBannerState(() => {
      if (!bannersInitialState) {
        const initialObject = {
          product_grid_pos_15: 0,
          product_grid_pos_39: 0,
          inside_grid: 0,
          above_footer: 0,
        };
        setCookie(BANNERS_STATE_COOKIE_NAME, JSON.stringify(initialObject));
        return initialObject;
      }

      return JSON.parse(bannersInitialState);
    });
    setIsLoading(false);
    loadFilters();
  }, [
    initialAscending,
    initialFilters,
    initialOrderBy,
    initialPage,
    initialProducts,
    initialQuery,
    initialTotal,
    bannersInitialState,
  ]);

  return (
    <ProductListContext.Provider
      value={{
        breadcrumbs,
        banners,
        bannersState,
        categorySlug,
        categoryThree,
        crossRewrite,
        ascending,
        brandName: brandName ?? null,
        brandSlug: brandSlug ?? null,
        filters,
        filterList,
        filteredFilterList,
        isFiltersListLoaded,
        isLoading,
        limit,
        orderBy,
        page,
        query,
        total,
        products,
        defaultCalendar,
        localeCalendar,
        shippingDeliverySettings,
        seo: seo ?? null,
        productMenuOpen,
        productMenuMobileSidebarOpen,
        setProductMenuMobileSidebarOpen,
        setProductMenuOpen,
        handleFetchFilters,
        handleFetchNewPage,
        setAscending,
        setFilters,
        setFilterList,
        setFilteredFilterList,
        setIsFiltersListLoaded,
        setIsLoading,
        setLimit,
        setOrderBy,
        setPage,
        setQuery,
        setTotal,
        setProducts,
        isRewritePage,
      }}
    >
      {children}
    </ProductListContext.Provider>
  );
};

export const useProductList = () => useContext(ProductListContext);
